import { useEffect, useState } from "react";
import { BbotButton, BbotModal, Input, notification, Space } from "top-component-library";
import { determineSwitchTextChoice, switchTextForChoice, toRawHostnameConfig } from "../utils";
import SwitchTextField from "./SwitchTextField";

// Mirrors enable-white-label-builder-dialog.html/enableWhiteLabelBuilderController: a
// wizard that skips straight to setConfig unless the hostname is already bosBuilder,
// in which case it first asks whether to change settings or disable instead.
const EnableWhiteLabelBuilderDialog = ({ visible, hostnameConfig, onClose, onSave }) => {
  const [step, setStep] = useState("setConfig");
  const [customerPrettyName, setCustomerPrettyName] = useState("");
  const [switchTextChoice, setSwitchTextChoice] = useState("default");
  const [switchText, setSwitchText] = useState("Switch Location");
  const [isSaving, setIsSaving] = useState(false);

  const startStep = hostnameConfig.configType === "bosBuilder" ? "intro" : "setConfig";

  useEffect(() => {
    if (!visible) return;
    setStep(startStep);
    setCustomerPrettyName(hostnameConfig.customer_pretty_name ?? "");
    const choice = determineSwitchTextChoice(hostnameConfig.switch_text);
    setSwitchTextChoice(choice);
    setSwitchText(hostnameConfig.switch_text ?? switchTextForChoice(choice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, hostnameConfig.hostname]);

  const onSwitchTextChoiceChange = (choice) => {
    setSwitchTextChoice(choice);
    setSwitchText(switchTextForChoice(choice));
  };

  const onConfirm = async () => {
    if (step === "disableWarning") {
      notification.info({
        message:
          "We do not support disabling this configuration through the Owner Portal at this time. Please submit a support request and we'll handle it as soon as we can.",
        duration: 8,
      });
      return;
    }

    setIsSaving(true);
    const succeeded = await onSave(
      {
        ...toRawHostnameConfig(hostnameConfig),
        customer_pretty_name: customerPrettyName,
        switch_text: switchText,
      },
      "Settings changed successfully!"
    );
    setIsSaving(false);
    if (succeeded) onClose();
  };

  const isInvalid =
    step === "setConfig" && (!customerPrettyName.trim() || (switchTextChoice === "custom" && !switchText.trim()));

  return (
    <BbotModal
      title={
        { intro: "What would you like to do?", setConfig: "BOS Settings", disableWarning: "Disable BOS Builder" }[step]
      }
      visible={visible}
      onCancel={onClose}
      footer={
        <Space>
          {startStep === "intro" && step !== "intro" && (
            <BbotButton onClick={() => setStep("intro")}>Start Over</BbotButton>
          )}
          <BbotButton onClick={onClose}>Close</BbotButton>
          {step !== "intro" && (
            <BbotButton type="primary" onClick={onConfirm} disabled={isInvalid} loading={isSaving}>
              Confirm
            </BbotButton>
          )}
        </Space>
      }
    >
      {step === "intro" && (
        <Space direction="vertical">
          <BbotButton type="primary" onClick={() => setStep("setConfig")}>
            Change my settings
          </BbotButton>
          <BbotButton onClick={() => setStep("disableWarning")}>Disable BOS Builder for this domain</BbotButton>
        </Space>
      )}

      {step === "setConfig" && (
        <div>
          <label htmlFor="white-label-customer-pretty-name">
            Customer Display Name (shown in the tab title and on receipts)
          </label>
          <Input
            id="white-label-customer-pretty-name"
            value={customerPrettyName}
            onChange={(e) => setCustomerPrettyName(e.target.value)}
          />
          <p className="margin-top-2">
            Please choose a setting for the <strong>&quot;Switch Location&quot;</strong> link on the menu page (link
            that takes you back to the landing page). Example: you might set it to{" "}
            <strong>&quot;Switch Room&quot;</strong> if the customer is a hotel.
          </p>
          <SwitchTextField
            choice={switchTextChoice}
            text={switchText}
            onChoiceChange={onSwitchTextChoiceChange}
            onTextChange={setSwitchText}
          />
        </div>
      )}

      {step === "disableWarning" && (
        <div>
          <p>
            Continuing will <strong>completely</strong> disable this feature and remove your saved settings. Guests
            visiting {hostnameConfig.hostname} will be shown the TOP by DoorDash homepage until you enable another
            configuration. If you change your mind, you can always re-enable the feature on the settings page.
          </p>
          <p>
            Press &apos;<strong>confirm</strong>&apos; to disable the config, or start over to go back.
          </p>
        </div>
      )}
    </BbotModal>
  );
};

export default EnableWhiteLabelBuilderDialog;
